import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Loader2, CalendarClock, Users, TrendingUp, Clock, CheckCircle2, Mail, Plus, Crown, Pencil } from "lucide-react";
import { clampDays } from "./DurationField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37";
const RED = "#C41E24";
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "America/Chicago" }) : "—");
const ago = (iso) => {
  if (!iso) return "Never";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "Today" : d === 1 ? "Yesterday" : `${d} days ago`;
};
const CODE_STATUS = { active: ["Active", "#22C55E"], paused: ["Paused", "#9CA3AF"], expired: ["Expired", RED] };
const ROW_STATUS = { active: ["Active", "#22C55E"], expiring: ["Expiring Soon", RED], expired: ["Expired", RED], converted: ["Converted", GOLD] };
const pill = "inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border";
const act = "inline-flex items-center justify-center gap-1 min-h-[36px] px-2.5 rounded-sm text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50";

export const StatusPill = ({ map, status, testid }) => {
  const [label, color] = map[status] || [status, "#999"];
  return <span data-testid={testid} className={pill} style={{ color, borderColor: `${color}66`, background: `${color}14` }}>{label}</span>;
};

const DaysBar = ({ r }) => {
  if (r.days_left === null) return <span className="text-white/35 text-xs">—</span>;
  const pct = Math.max(0, Math.min(100, (r.days_left / (r.total_days || 7)) * 100));
  const color = r.days_left <= 2 ? RED : GOLD;
  return (
    <div className="min-w-[90px]">
      <div data-testid={`td-days-${r.id}`} className="font-cover text-xl leading-none" style={{ color }}>{r.days_left}<span className="text-[10px] font-sans text-white/40 ml-1">days</span></div>
      <div className="h-1 mt-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${pct}%`, background: color }} /></div>
    </div>
  );
};

const Summary = ({ s }) => (
  <div data-testid="td-summary" className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
    {[["active", "Active trials", s.active, GOLD, Users], ["converted", "Converted to member", s.converted, "#22C55E", CheckCircle2], ["expired", "Expired, no convert", s.expired, RED, Clock], ["rate", "Conversion rate", `${s.conversion_rate}%`, GOLD, TrendingUp]].map(([k, l, v, c, I]) => (
      <div key={k} data-testid={`td-summary-${k}`} className="rounded-lg border border-white/10 bg-[#141414] p-3">
        <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.2em] text-white/45"><I size={11} style={{ color: c }} /> {l}</div>
        <div className="font-cover text-3xl mt-1" style={{ color: c }}>{v}</div>
      </div>
    ))}
  </div>
);

const RowActions = ({ r, onAction, busy }) => {
  const [tier, setTier] = useState("academy");
  const done = r.status === "converted";
  return (
    <div className="flex flex-wrap gap-1.5">
      {!done && <>
        <button data-testid={`td-extend3-${r.id}`} disabled={!!busy} onClick={() => onAction(r, "extend", { days: 3 })} className={`${act} border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10`}><Plus size={11} />3d</button>
        <button data-testid={`td-extend7-${r.id}`} disabled={!!busy} onClick={() => onAction(r, "extend", { days: 7 })} className={`${act} border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10`}><Plus size={11} />7d</button>
        <span className="inline-flex">
          <select data-testid={`td-convert-tier-${r.id}`} value={tier} onChange={(e) => setTier(e.target.value)} className="min-h-[36px] bg-[#0d0d0d] border border-white/15 border-r-0 rounded-l-sm text-[16px] sm:text-[11px] text-white px-1.5">
            <option value="academy">Technical Track</option><option value="elite">Elite</option>
          </select>
          <button data-testid={`td-convert-${r.id}`} disabled={!!busy} onClick={() => onAction(r, "convert", { tier })} className={`${act} rounded-l-none bg-[#22C55E]/15 border border-[#22C55E]/50 text-[#22C55E] hover:bg-[#22C55E]/25`}><Crown size={11} /> Convert</button>
        </span>
      </>}
      {r.nudged_at ? <span data-testid={`td-nudged-${r.id}`} className={`${act} border border-white/10 text-white/50 cursor-default`} title={new Date(r.nudged_at).toLocaleString()}><CheckCircle2 size={11} className="text-[#22C55E]" /> Nudged {day(r.nudged_at)}</span>
        : !done && <button data-testid={`td-nudge-${r.id}`} disabled={!!busy || !r.can_nudge} onClick={() => onAction(r, "nudge")} title={r.can_nudge ? "Send the trial-ending email" : "Available when 3 days or fewer remain"} className={`${act} bg-[#C41E24] hover:bg-[#A0161D] text-white disabled:bg-white/5 disabled:text-white/35`}><Mail size={11} /> Nudge</button>}
      {busy === r.id && <Loader2 size={14} className="animate-spin text-white/50 self-center" />}
    </div>
  );
};

const PlayerRow = ({ r, onAction, busy }) => {
  const hot = r.status === "expiring" || r.status === "expired";
  return (
    <tr data-testid={`td-row-${r.id}`} className="border-b border-white/5 align-top" style={{ boxShadow: hot ? `inset 3px 0 0 ${RED}` : undefined, background: hot ? "rgba(196,30,36,0.04)" : undefined }}>
      <td className="py-3 pl-4 pr-3 min-w-[180px]"><div className="font-bold text-sm text-white">{r.player_name}</div><div className="text-[11px] text-white/45 break-all">{r.parent_email}</div></td>
      <td className="py-3 px-3 text-xs text-white/70 whitespace-nowrap">{day(r.activated_at)}</td>
      <td className="py-3 px-3 text-xs text-white/70 whitespace-nowrap">{r.trial_length}-day{r.extended_days > 0 && <span className="block text-[10px] text-[#D4AF37]">+{r.extended_days} extended</span>}</td>
      <td className="py-3 px-3"><DaysBar r={r} /></td>
      <td className="py-3 px-3 text-xs text-white/70 whitespace-nowrap">{ago(r.last_active)}</td>
      <td data-testid={`td-engagement-${r.id}`} className="py-3 px-3 text-xs text-white/70 whitespace-nowrap"><b className="text-white">{r.drills}</b> drills · <b className="text-white">{r.videos}</b> videos</td>
      <td className="py-3 px-3"><StatusPill map={ROW_STATUS} status={r.status} testid={`td-status-${r.id}`} />{r.conversion_type === "coach_granted" && <div className="text-[9px] text-white/40 mt-1 uppercase tracking-wider">Coach Granted</div>}</td>
      <td className="py-3 px-3 pr-4"><RowActions r={r} onAction={onAction} busy={busy} /></td>
    </tr>
  );
};

const ExpiryEditor = ({ h, token, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(h.expires_at ? new Date(h.expires_at).toLocaleDateString("en-CA", { timeZone: "America/Chicago" }) : "");
  const save = async () => {
    try { await axios.patch(`${API}/coach/trial-codes/${h.id}`, { expires_on: val }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(val ? "Expiry date saved." : "Expiry removed."); setEdit(false); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save the date."); }
  };
  if (!edit) return (
    <span data-testid="td-expires" className="inline-flex items-center gap-1.5">{h.expires_at ? `Expires ${day(h.expires_at)}` : "No expiration"}
      {h.kind === "code" && <button data-testid="td-expiry-edit" onClick={() => setEdit(true)} aria-label="Edit expiry" className="w-8 h-8 inline-flex items-center justify-center text-white/45 hover:text-[#D4AF37]"><Pencil size={12} /></button>}</span>
  );
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <input data-testid="td-expiry-input" type="date" value={val} onChange={(e) => setVal(e.target.value)} className="bg-[#0d0d0d] border border-white/15 rounded px-2 h-9 text-[16px] sm:text-xs text-white [color-scheme:dark]" />
      <button data-testid="td-expiry-save" onClick={save} className={`${act} bg-[#D4AF37] text-black`}>Save</button>
      {val && <button data-testid="td-expiry-clear" onClick={() => setVal("")} className={`${act} border border-white/15 text-white/60`}>Clear</button>}
      <button data-testid="td-expiry-cancel" onClick={() => setEdit(false)} className={`${act} text-white/45`}>Cancel</button>
    </span>
  );
};

const DurationEditor = ({ h, activeCount, token, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(String(h.trial_days));
  const cur = h.current_activation_days || [];
  const differs = cur.length > 0 && (cur.length > 1 || cur[0] !== h.trial_days);
  const save = async () => {
    const n = clampDays(val);
    if (n === h.trial_days) { setEdit(false); return; }
    if (!window.confirm(`This change applies to new activations only. ${activeCount} player${activeCount === 1 ? "" : "s"} currently on this trial ${activeCount === 1 ? "is" : "are"} unaffected.`)) return;
    try { await axios.patch(`${API}/coach/trial-codes/${h.id}`, { days: n }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(`New activations now get ${n} days.`); setEdit(false); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save the duration."); }
  };
  if (edit) return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <input data-testid="td-duration-input" type="number" inputMode="numeric" min="1" max="365" value={val} onChange={(e) => setVal(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))} className="w-20 bg-[#0d0d0d] border border-white/15 rounded px-2 h-9 text-[16px] sm:text-xs text-white" />
      <span className="text-white/40">days</span>
      <button data-testid="td-duration-save" onClick={save} className={`${act} bg-[#D4AF37] text-black`}>Save</button>
      <button data-testid="td-duration-cancel" onClick={() => setEdit(false)} className={`${act} text-white/45`}>Cancel</button>
    </span>
  );
  return (
    <span data-testid="td-duration" className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
      {differs ? <>
        <span data-testid="td-duration-current">Current activations: <b className="text-white">{cur.join(" / ")} days</b></span>
        <span data-testid="td-duration-new">New activations: <b className="text-[#D4AF37]">{h.trial_days} days</b></span>
      </> : <span>Duration: <b className="text-[#D4AF37]">{h.trial_days} days</b></span>}
      {h.kind === "code" && <button data-testid="td-duration-edit" onClick={() => { setVal(String(h.trial_days)); setEdit(true); }} aria-label="Edit duration" className="w-8 h-8 inline-flex items-center justify-center text-white/45 hover:text-[#D4AF37]"><Pencil size={12} /></button>}
    </span>
  );
};

const DurationHistory = ({ items }) => items?.length > 0 && (
  <details data-testid="td-duration-history" className="mt-2 text-[11px] text-white/50">
    <summary className="cursor-pointer min-h-[32px] inline-flex items-center uppercase tracking-wider font-bold text-white/45 hover:text-white">Duration history ({items.length})</summary>
    <ul className="mt-1 space-y-0.5">{[...items].reverse().map((x) => <li key={x.at}>{new Date(x.at).toLocaleString(undefined, { timeZone: "America/Chicago" })} — {x.from} → <b className="text-white/80">{x.to} days</b></li>)}</ul>
  </details>
);

export default function TrialCodeDetail({ token, kind, id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const url = kind === "source" ? `${API}/coach/trial-sources/${id}/detail` : `${API}/coach/trial-codes/${id}/detail`;
  const load = useCallback(() => axios.get(url, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setD(data)).catch(() => setD({ error: true })), [url, token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const k = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  const onAction = async (r, action, body = {}) => {
    if (action === "convert" && !window.confirm(`Grant ${body.tier === "elite" ? "Elite" : "Technical Track"} membership to ${r.player_name}? No payment is taken.`)) return;
    setBusy(r.id);
    try {
      const { data } = await axios.post(`${API}/coach/trial-players/${r.id}/${action}`, body, auth);
      toast.success(action === "extend" ? `Added ${body.days} days for ${r.player_name}.` : action === "convert" ? `${r.player_name} is now a member${data.email_sent ? " — welcome email sent" : ""}.` : `Nudge sent to ${r.parent_email}.`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong."); }
    setBusy("");
  };
  const h = d?.header;
  return createPortal(
    <div data-testid="trial-code-detail" role="dialog" aria-modal="true" className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="w-full max-w-6xl h-full overflow-y-auto bg-[#0b0b0b] border-l border-[#D4AF37]/25 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[#0b0b0b]/95 backdrop-blur border-b border-white/10 px-4 sm:px-6 py-4 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            {!d ? <div className="text-white/40 text-sm">Loading…</div> : !h ? null : <>
              <div className="flex flex-wrap items-center gap-2">
                <h2 data-testid="td-name" className="font-cover text-3xl sm:text-4xl uppercase leading-none" style={{ color: kind === "source" ? "#fff" : GOLD }}>{h.name}</h2>
                <StatusPill map={CODE_STATUS} status={h.status} testid="td-code-status" />
                {h.expiring_soon && <span data-testid="td-expiring-soon" className={pill} style={{ color: "#FACC15", borderColor: "#FACC1566", background: "#FACC1514" }}>Expiring Soon</span>}
                {kind === "source" && <span className={pill} style={{ color: GOLD, borderColor: `${GOLD}66` }}>Flyer Source</span>}
              </div>
              <div data-testid="td-source-label" className="text-sm text-white/70 mt-1">{h.source_label}</div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-[11px] text-white/50 font-mono uppercase tracking-wider">
                <span data-testid="td-uses"><b className="text-white">{h.uses}</b>{h.max_uses ? ` / ${h.max_uses}` : ""} activations{!h.max_uses && kind === "code" ? " · no limit" : ""}</span>
                <DurationEditor key={`${h.trial_days}-${(h.history || []).length}`} h={h} activeCount={d.summary.active} token={token} onSaved={() => { load(); onChanged?.(); }} />
                <span className="inline-flex items-center gap-1"><CalendarClock size={11} /> {kind === "source" ? "First signup" : "Created"} {day(h.created_at)}</span>
                <ExpiryEditor key={h.expires_at} h={h} token={token} onSaved={() => { load(); onChanged?.(); }} />
              </div>
              <DurationHistory items={h.history} />
            </>}
          </div>
          <button data-testid="td-close" onClick={onClose} aria-label="Close" className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white"><X size={18} /></button>
        </div>
        <div className="px-4 sm:px-6 py-5">
          {d?.error && <p className="text-white/60" data-testid="td-error">Couldn't load this trial code.</p>}
          {d && !d.error && <>
            <Summary s={d.summary} />
            {d.players.length === 0 ? <div data-testid="td-empty" className="text-center text-sm text-white/40 py-12 border border-dashed border-white/10 rounded-xl">No one has started a trial from this {kind === "source" ? "source" : "code"} yet.</div> : (
              <div className="rounded-xl border border-white/10 bg-[#111111] overflow-x-auto">
                <table data-testid="td-table" className="w-full min-w-[980px] text-left">
                  <thead><tr className="border-b border-white/10 text-[9px] font-bold uppercase tracking-[0.2em] text-white/40">
                    {["Player / Parent", "Activated", "Length", "Days left", "Last active", "Engagement", "Status", "Actions"].map((t, i) => <th key={t} className={`py-2.5 px-3 font-bold ${i === 0 ? "pl-4" : ""}`}>{t}</th>)}
                  </tr></thead>
                  <tbody>{d.players.map((r) => <PlayerRow key={r.id} r={r} onAction={onAction} busy={busy} />)}</tbody>
                </table>
              </div>
            )}
          </>}
        </div>
      </div>
    </div>,
    document.body
  );
}
